'use client';

import '../styles/globals.css';
import '../styles/ai-components.css';
import React from 'react';
import { useState, useEffect } from 'react';
import { cn } from '../lib/utils';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from 'components/ai-elements/conversation';
import { Message } from 'components/ai-elements/message';
import { Response } from 'components/ai-elements/response';
import { Loader } from 'components/ai-elements/loader';
import { Actions, Action } from 'components/ai-elements/actions';
import {
  InlineCitation,
  InlineCitationCard,
  InlineCitationCardBody,
  InlineCitationCardTrigger,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselItem,
  InlineCitationCarouselHeader,
  InlineCitationCarouselIndex,
  InlineCitationSource,
  InlineCitationQuote,
} from 'components/ai-elements/inline-citation';
import {
  PromptInput,
  PromptInputAttachments,
  PromptInputBody,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
  usePromptInputAttachments,
} from 'components/ai-elements/prompt-input';
import { RefreshCcwIcon, CopyIcon, CheckIcon } from 'lucide-react';
import InconsistencyPreview from './DocumentPage/tabs/InconsistencyPreview';

import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import logo from 'icons/LOGO.svg'
import { useAuthInfo } from '@propelauth/react';

interface MessagePart {
  type: string;
  text: string;
  fileData?: {
    id?: string;
    filename?: string;
    mediaType?: string;
    url?: string;
  };
}

interface Citation {
  number: string;
  title: string;
  url?: string;
  description?: string;
  quote?: string;
  filename?: string;
  lineNumbers?: string;
  page?: string;
  section?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  parts: MessagePart[];
  citations?: Citation[];
}



// Custom Response component that handles citations
const ResponseWithCitations = ({
  children,
  citations,
  fileBlobs,
  onOpenDocument,
  documentViewer,
  setDocumentViewer
}: {
  children: string;
  citations?: Citation[];
  fileBlobs?: { [filename: string]: string };
  onOpenDocument?: (fileUrl: string, filename: string, title: string, selectedText?: string, page?: string) => void;
  documentViewer?: { isOpen: boolean; fileUrl: string; title: string; filename: string; selectedText?: string; page?: string; extension?: string; } | null;
  setDocumentViewer?: React.Dispatch<React.SetStateAction<{ isOpen: boolean; fileUrl: string; title: string; filename: string; selectedText?: string; page?: string; extension?: string; } | null>>;
}) => {
  if (!citations || citations.length === 0) {
    return <Response>{children}</Response>;
  }

  // Parse the content and replace citation markers with InlineCitation components
  const citationRegex = /\[(\d+)\]/g;
  const elements: React.ReactNode[] = [];


  let lastIndex = 0;
  let match;

  while ((match = citationRegex.exec(children)) !== null) {
    // Add text before the citation
    if (match.index > lastIndex) {
      const textBefore = children.substring(lastIndex, match.index);
      if (textBefore.trim()) {
        elements.push(<Response key={`text-${lastIndex}`}>{textBefore}</Response>);
      }
    }

    // Add the citation
    const citationNumber = match[1];
    const citation = citations.find(c => c.number === citationNumber);

    if (citation) {
      const handleCitationClick = () => {
        const blobUrl = fileBlobs?.[citation.filename || ''];
        const newSelectedText = citation.quote
          ? citation.quote.split(/[.!?]+/)[0]
          : undefined;

        if (blobUrl) {
          // Check if the same document is already open
          if (documentViewer && documentViewer.isOpen && documentViewer.filename === citation.filename && setDocumentViewer) {
            // Same document is open - just update selectedText dynamically
            setDocumentViewer((prev: { isOpen: boolean; fileUrl: string; title: string; filename: string; selectedText?: string; page?: string; extension?: string; } | null) => prev ? {
              ...prev,
              selectedText: newSelectedText,
              page: citation.page || prev.page
            } : null);
          } else if (onOpenDocument) {
            // New document or no document open - use the original flow
            onOpenDocument(
              blobUrl,
              citation.filename || 'document',
              citation.title || citation.filename || 'Document',
              newSelectedText,
              citation.page
            );
          }
        }
      };

      elements.push(
        <InlineCitation key={`citation-${match.index}`}>
          <InlineCitationCard>
            <InlineCitationCardTrigger
              sources={[`file://${citation.filename || 'document'}`]}
              onClick={handleCitationClick}
              style={{
                backgroundColor: '#1F477D',
                color: 'white',
                cursor: fileBlobs?.[citation.filename || ''] ? 'pointer' : 'default'
              }}
            />
            <InlineCitationCardBody>
              <InlineCitationCarousel>
                <InlineCitationCarouselHeader>
                  <InlineCitationCarouselIndex />
                </InlineCitationCarouselHeader>
                <InlineCitationCarouselContent>
                  <InlineCitationCarouselItem>
                    <InlineCitationSource
                      title={citation.title || citation.filename}
                      description={citation.description}
                    />
                    {citation.quote && (
                      <InlineCitationQuote>
                        {citation.quote}
                        {citation.lineNumbers && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            Lines: {citation.lineNumbers}
                          </div>
                        )}
                      </InlineCitationQuote>
                    )}
                    {fileBlobs?.[citation.filename || ''] && (
                      <div style={{
                        marginTop: '0.5rem',
                        padding: '0.5rem',
                        backgroundColor: '#f1f5f9',
                        borderRadius: '0.375rem',
                        fontSize: '0.75rem',
                        color: '#64748b'
                      }}>
                        💡 Click the citation number to view document side-by-side
                      </div>
                    )}
                  </InlineCitationCarouselItem>
                </InlineCitationCarouselContent>
              </InlineCitationCarousel>
            </InlineCitationCardBody>
          </InlineCitationCard>
        </InlineCitation>
      );
    }

    // Skip any punctuation immediately following the citation
    let nextIndex = citationRegex.lastIndex;
    while (nextIndex < children.length && /[.!?;,:]/.test(children[nextIndex])) {
      nextIndex++;
    }
    lastIndex = nextIndex;
  }

  // Add any remaining text after the last citation
  if (lastIndex < children.length) {
    const remainingText = children.substring(lastIndex);
    if (remainingText.trim()) {
      elements.push(<Response key={`text-${lastIndex}`}>{remainingText}</Response>);
    }
  }

  // If no citations found, just return the original text as Response
  if (elements.length === 0) {
    return <Response>{children}</Response>;
  }

  return <>{elements}</>;
};

// Custom file upload button component that uses the PromptInput context
const FileUploadButton = () => {
  const attachments = usePromptInputAttachments();

  return (
    <button
      type="button"
      onClick={() => {
        console.log('File button clicked');
        attachments.openFileDialog();
      }}
      className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
    >
      <AddIcon style={{ fontSize: '1.25rem' }} />
    </button>
  );
};

// Custom attachment component that shows filenames properly
const CustomAttachment = ({ data }: { data: any }) => {
  const attachments = usePromptInputAttachments();

  return (
    <div className="group relative flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 max-w-xs">
      {data.mediaType?.startsWith("image/") && data.url ? (
        <img
          alt={data.filename || "image"}
          className="h-8 w-8 rounded object-cover flex-shrink-0"
          src={data.url}
        />
      ) : (
        <div className="flex h-8 w-8 items-center justify-center text-gray-400 flex-shrink-0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2Z"/>
            <polyline points="14,2 14,8 20,8"/>
          </svg>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">
          {data.filename || 'Unknown file'}
        </div>
        <div className="text-xs text-gray-500">
          {data.mediaType || 'Unknown type'}
        </div>
      </div>
      <button
        onClick={() => attachments.remove(data.id)}
        className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
        type="button"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
};

export default function ChatPage(): JSX.Element {
  const apiBaseUrl: string | undefined = process.env.REACT_APP_API_URL_BASE;
  const [messages, setMessages] = useState<Message[]>([]);
  const [status, setStatus] = useState<'ready' | 'submitted' | 'streaming'>('ready');
  const [inputKey, setInputKey] = useState<number>(0);
  const [fileBlobs, setFileBlobs] = useState<{ [filename: string]: string }>({});
  const [documentViewer, setDocumentViewer] = useState<{
    isOpen: boolean;
    fileUrl: string;
    title: string;
    filename: string;
    selectedText?: string;
    page?: string;
    extension?: string;
  } | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [orgAccessToken, setOrgAccessToken] = useState<any>(null);
  const authInfo = useAuthInfo();

  // Get access token once when component mounts
  useEffect(() => {
    async function fetchOrgAccessToken() {
      try {
        const orgs = authInfo?.orgHelper?.getOrgs();
        const org = orgs?.[0]?.orgId;
        if (!org) {
          console.error('No organization found');
          return;
        }
        const token = await authInfo?.tokens?.getAccessTokenForOrg(org);
        setOrgAccessToken(token);
      } catch (error) {
        console.error('Failed to get org access token:', error);
      }
    }

    if (authInfo?.orgHelper && authInfo?.tokens) {
      fetchOrgAccessToken();
    }
  }, []);

  const handleOpenDocument = (
    fileUrl: string,
    filename: string,
    title: string,
    selectedText?: string,
    page?: string
  ) => {
    // Determine file extension from filename
    const getFileExtension = (filename: string): string => {
      const extension = filename.toLowerCase().split('.').pop() || '';

      // Map various extensions to supported types
      if (extension === 'pdf') return 'pdf';
      if (extension === 'docx') return 'docx';
      if (extension === 'doc') return 'docx'; // Treat .doc as docx
      if (extension === 'jpg' || extension === 'jpeg') return 'pdf'; // Display images as PDF
      if (extension === 'png') return 'pdf'; // Display images as PDF

      // Default fallback
      return 'pdf';
    };

    const fileExtension = getFileExtension(filename);

    setDocumentViewer({
      isOpen: true,
      fileUrl,
      title,
      filename,
      selectedText,
      page,
      extension: fileExtension
    });
  };

  const handleCloseDocument = () => {
    setDocumentViewer(null);
  };

  const handleCopy = (messageId: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedMessageId(messageId);
    // Reset the copy state after 2 seconds
    setTimeout(() => {
      setCopiedMessageId(null);
    }, 2000);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
  };

  const handleSubmit = (message: PromptInputMessage) => {
    console.log('PromptInput submit:', message);
    console.log('Full message object:', JSON.stringify(message, null, 2));

    const hasText = Boolean(message.text);
    const hasAttachments = Boolean(message.files?.length);

    console.log('Has text:', hasText, 'Has attachments:', hasAttachments);
    console.log('Files array:', message.files);
    console.log('Files length:', message.files?.length);

    if (message.files && message.files.length > 0) {
      message.files.forEach((file, index) => {
        console.log(`File ${index}:`, {
          filename: file.filename,
          mediaType: file.mediaType,
          url: file.url,
          type: file.type
        });
      });
    }

    if (!(hasText || hasAttachments)) {
      console.log('No text or attachments, returning early');
      return;
    }

    // Use custom message handling for your API, passing both text and files
    handleSendMessage(message.text || 'Sent with attachments', message.files);

    // Clear the input value and force PromptInput to reset
    setInputValue('');
    setInputKey(prev => prev + 1);
  };

  // Custom API message handling
  const handleSendMessage = async (messageText: string, files?: any[]): Promise<void> => {
    setStatus('submitted');

    // Add user message first with file information if present
    const messageParts = [{ type: 'text', text: messageText }];

   


    const myHeader = new Headers()
    myHeader.append('Authorization', 'Bearer ' + orgAccessToken?.accessToken)

    // Add file information to the message parts
    if (files && files.length > 0) {
      files.forEach(file => {
        messageParts.push({
          type: 'file',
          text: `📎 ${file.filename || 'Unknown file'}`,
          fileData: {
            filename: file.filename,
            mediaType: file.mediaType,
            url: file.url
          }
        } as MessagePart);
      });
    }

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      parts: messageParts
    };

    setMessages(prevMessages => [...prevMessages, userMessage]);

    // Create assistant message placeholder
    const assistantMessageId: string = crypto.randomUUID();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      parts: [{ type: 'text', text: '' }]
    };

    setMessages(prevMessages => [...prevMessages, assistantMessage]);
    setStatus('streaming');

    try {
      console.log('Sending message with files:', files);

      let response: Response;

      if (files && files.length > 0) {
        // Convert PromptInput files to actual File objects
        const actualFiles: File[] = [];
        const newFileBlobs: { [filename: string]: string } = { ...fileBlobs };

        for (let i = 0; i < files.length; i++) {
          const promptFile = files[i];
          console.log('Processing PromptInput file:', promptFile);

          if (promptFile.url && promptFile.url.startsWith('blob:')) {
            try {
              // Store the blob URL for later use
              if (promptFile.filename) {
                newFileBlobs[promptFile.filename] = promptFile.url;
              }

              // Fetch the blob data from the blob URL
              const blobResponse = await fetch(promptFile.url);
              const blob = await blobResponse.blob();

              // Create a File object from the blob
              const file = new File([blob], promptFile.filename || `file_${i}`, {
                type: promptFile.mediaType || 'application/octet-stream'
              });

              actualFiles.push(file);
              console.log('Created File object:', {
                name: file.name,
                type: file.type,
                size: file.size
              });
            } catch (error) {
              console.error('Error converting blob to file:', error);
            }
          }
        }

        // Update file blobs state
        setFileBlobs(newFileBlobs);

        if (actualFiles.length > 0) {
          // Use FormData for file uploads
          const formData = new FormData();

          // Add the message data as JSON
          const messageData = {
            id: crypto.randomUUID(),
            messages: [...messages, userMessage],
            trigger: 'submit-message'
          };
          formData.append('data', JSON.stringify(messageData));

          // Add files to FormData
          actualFiles.forEach((file, index) => {
            console.log(`Adding file_${index}:`, file.name, file.type, file.size);
            formData.append(`file_${index}`, file);
          });

          // Log FormData contents for debugging
          console.log('FormData contents:');
          for (let [key, value] of (formData as any).entries()) {
            if (value instanceof File) {
              console.log(`${key}: File(${value.name}, ${value.type}, ${value.size} bytes)`);
            } else {
              console.log(`${key}: ${value}`);
            }
          }

          console.log('Sending FormData with files');

          response = await fetch(`${apiBaseUrl}/api/custom-chat`, {
            method: 'POST',
            body: formData, // No Content-Type header - browser sets it automatically for FormData
            headers: myHeader,
          });
        } else {
          throw new Error('Failed to convert files for upload');
        }
      } else {
        // JSON request for text-only messages
        const requestBody = {
          id: crypto.randomUUID(),
          messages: [...messages, userMessage],
          trigger: 'submit-message'
        };

        response = await fetch(`${apiBaseUrl}/api/custom-chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${orgAccessToken?.accessToken}`
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (response.ok && response.body) {
        const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();
        const decoder: TextDecoder = new TextDecoder();
        let accumulatedText: string = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk: string = decoder.decode(value, { stream: true });

          // Parse each JSON chunk - handle different possible response formats
          const lines: string[] = chunk.split('\n').filter(line => line.trim());

          for (const line of lines) {
            try {
              const jsonChunk: any = JSON.parse(line);
              console.log('Parsed chunk:', jsonChunk);

              // Handle different response formats
              let textToAdd = '';
              let citationsToAdd: Citation[] = [];

              if (jsonChunk.parts && jsonChunk.parts[0] && jsonChunk.parts[0].text) {
                textToAdd = jsonChunk.parts[0].text;
              } else if (jsonChunk.text) {
                textToAdd = jsonChunk.text;
              } else if (jsonChunk.content) {
                textToAdd = jsonChunk.content;
              } else if (typeof jsonChunk === 'string') {
                textToAdd = jsonChunk;
              }

              // Handle citations
              if (jsonChunk.citations && Array.isArray(jsonChunk.citations)) {
                citationsToAdd = jsonChunk.citations;
              }

              if (textToAdd) {
                accumulatedText += textToAdd;

                // Update the assistant message with accumulated text and citations
                setMessages(prevMessages =>
                  prevMessages.map(msg => {
                    if (msg.id === assistantMessageId) {
                      // Merge citations, avoiding duplicates
                      const existingCitations = msg.citations || [];
                      const mergedCitations = [...existingCitations];

                      citationsToAdd.forEach(newCitation => {
                        if (!existingCitations.find(c => c.number === newCitation.number)) {
                          mergedCitations.push(newCitation);
                        }
                      });

                      return {
                        ...msg,
                        parts: [{ type: 'text', text: accumulatedText }],
                        citations: mergedCitations.length > 0 ? mergedCitations : msg.citations
                      };
                    }
                    return msg;
                  })
                );
              }
            } catch (parseError) {
              console.warn('Failed to parse chunk:', line, parseError);
              // Try to handle as plain text if JSON parsing fails
              if (line.trim()) {
                accumulatedText += line;
                setMessages(prevMessages =>
                  prevMessages.map(msg =>
                    msg.id === assistantMessageId
                      ? { ...msg, parts: [{ type: 'text', text: accumulatedText }] }
                      : msg
                  )
                );
              }
            }
          }
        }
        setStatus('ready');
      } else {
        console.error('API call failed:', response.status, await response.text());
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, parts: [{ type: 'text', text: 'Sorry, there was an error processing your request.' }] }
              : msg
          )
        );
        setStatus('ready');
      }
    } catch (error) {
      console.error('Error calling API:', error);
      setStatus('ready');
    }
  };

  // Suggestion buttons for empty state
  const suggestions = [
    "Summarize a non-clinical study report",
    "Summarize a data table",
    "Write more concisely",
    "Summarize documents"
  ];

  return (
    <div className="flex h-full bg-gray-50">
      {/* Main chat area */}
      <div className={`flex flex-col ${documentViewer ? 'w-1/2' : 'w-full'} transition-all duration-300 max-h-screen overflow-y-auto`}>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
        {messages.length === 0 ? (
          /* Empty state with suggestions */
          <div className="max-w-2xl w-full text-center">
            <h1 className="text-2xl font-medium text-gray-800 mb-8">
              What do you need help with today?
            </h1>

            {/* Suggestion buttons */}
            <div className="flex flex-wrap gap-3 justify-center mb-12">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Chat messages */
          <div className="max-w-4xl w-full">
            <Conversation className="h-full">
              <ConversationContent className="space-y-6">
                {messages.map((message: any) => (
                  <div key={message.id}>
                    <Message
                      from={message.role}
                      style={{
                        display: 'flex',
                        gap: '0.75rem',
                        marginBottom: '1rem',
                        alignItems: 'flex-start',
                        flexDirection: message.role === 'user' ? 'row' : 'row',
                        justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {/* Avatar for assistant messages */}
                      {message.role === 'assistant' && (
                        <div
                          className="message-avatar"
                          style={{
                            width: '2rem',
                            height: '2rem',
                            borderRadius: '50%',
                            backgroundColor: '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            overflow: 'hidden'
                          }}
                        >
                          <img
                            src={logo}
                            alt="AI"
                            style={{
                              width: '1.5rem',
                              height: '1.5rem',
                              objectFit: 'contain'
                            }}
                          />
                        </div>
                      )}

                      <div
                        style={{
                          fontSize: '0.875rem',
                          lineHeight: '1.625',
                          padding: message.role === 'assistant' ? '0.5rem' : '0.75rem 1rem',
                          borderRadius: '1rem',
                          maxWidth: '70%',
                          wordWrap: 'break-word',
                          backgroundColor: message.role === 'user' ? '#e3f2fd' : '#f1f5f9',
                          color: message.role === 'user' ? '#1e293b' : '#1e293b',
                          border: message.role === 'assistant' ? '1px solid #e2e8f0' : '1px solid #e3f2fd',
                          borderBottomRightRadius: message.role === 'user' ? '0.25rem' : '1rem',
                          borderBottomLeftRadius: message.role === 'assistant' ? '0.25rem' : '1rem'
                        }}
                      >
                        {/* Render message parts */}
                        {message.parts && message.parts.length > 0 ? (
                          <div>
                            {message.parts.map((part: any, index: number) => {
                              if (part.type === 'text') {
                                // Use ResponseWithCitations for assistant messages, styled span for user messages
                                return message.role === 'assistant' ? (
                                  <ResponseWithCitations
                                    key={index}
                                    citations={message.citations}
                                    fileBlobs={fileBlobs}
                                    onOpenDocument={handleOpenDocument}
                                    documentViewer={documentViewer}
                                    setDocumentViewer={setDocumentViewer}
                                  >
                                    {part.text}
                                  </ResponseWithCitations>
                                ) : (
                                  <span
                                    key={index}
                                    style={{
                                      color: '#1e293b',
                                      whiteSpace: 'pre-wrap'
                                    }}
                                  >
                                    {part.text}
                                  </span>
                                );
                              } else if (part.type === 'file') {
                                return (
                                  <div key={index} style={{
                                    marginTop: '0.5rem',
                                    padding: '0.5rem',
                                    backgroundColor: message.role === 'user' ? 'rgba(227, 242, 253, 0.5)' : '#f1f5f9',
                                    borderRadius: '0.5rem',
                                    border: message.role === 'user' ? '1px solid rgba(227, 242, 253, 0.8)' : '1px solid #e2e8f0',
                                    fontSize: '0.8rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                  }}>
                                    <span>📎</span>
                                    <span style={{
                                      fontWeight: '500',
                                      color: message.role === 'user' ? '#1e293b' : '#64748b',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      maxWidth: '200px'
                                    }}>
                                      {part.fileData?.filename || 'Unknown file'}
                                    </span>
                                    {part.fileData?.mediaType && (
                                      <span style={{
                                        fontSize: '0.7rem',
                                        opacity: 0.7,
                                        color: message.role === 'user' ? '#64748b' : '#94a3b8'
                                      }}>
                                        ({part.fileData.mediaType})
                                      </span>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        ) : (
                          // Fallback for messages without parts structure
                          message.role === 'assistant' ? (
                            <ResponseWithCitations
                              citations={message.citations}
                              fileBlobs={fileBlobs}
                              onOpenDocument={handleOpenDocument}
                              documentViewer={documentViewer}
                              setDocumentViewer={setDocumentViewer}
                            >
                              {message.content || message.text || JSON.stringify(message)}
                            </ResponseWithCitations>
                          ) : (
                            <span style={{ color: '#1e293b', whiteSpace: 'pre-wrap' }}>
                              {message.content || message.text || JSON.stringify(message)}
                            </span>
                          )
                        )}
                      </div>

                      {/* Avatar for user messages */}
                      {message.role === 'user' && (
                        <div
                          className="message-avatar"
                          style={{
                            width: '2rem',
                            height: '2rem',
                            borderRadius: '50%',
                            backgroundColor: '#6b7280',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <PersonIcon style={{ fontSize: '1.2rem' }} />
                        </div>
                      )}
                    </Message>

                    {/* Action buttons for assistant messages */}
                    {message.role === 'assistant' && message.parts[0]?.text && message.parts[0].text.trim().length > 0 && (
                      <Actions
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginTop: '0.5rem',
                          marginLeft: '2.75rem'
                        }}
                      >
                        <Action
                          onClick={() => handleSendMessage(message.parts[0]?.text || '', [])}
                          label="Retry"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '2rem',
                            height: '2rem',
                            color: '#6b7280',
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          <RefreshCcwIcon className="size-3" />
                        </Action>
                        <Action
                          onClick={() => handleCopy(message.id, message.parts[0]?.text || '')}
                          label={copiedMessageId === message.id ? "Copied!" : "Copy"}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '2rem',
                            height: '2rem',
                            color: copiedMessageId === message.id ? '#10b981' : '#6b7280',
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            border: `1px solid ${copiedMessageId === message.id ? '#10b981' : '#e5e7eb'}`
                          }}
                        >
                          {copiedMessageId === message.id ? (
                            <CheckIcon className="size-3" />
                          ) : (
                            <CopyIcon className="size-3" />
                          )}
                        </Action>
                      </Actions>
                    )}
                  </div>
                ))}
                {status !== 'ready' && <Loader />}
              </ConversationContent>
              <ConversationScrollButton />
            </Conversation>
          </div>
        )}
      </div>

      {/* Bottom input area */}
      <div className="p-4">
        <div className="max-w-4xl mx-auto">
          <PromptInput
            key={inputKey}
            onSubmit={handleSubmit}
            className="bg-white border border-gray-300 rounded-2xl shadow-lg"
            globalDrop
            multiple
            maxFiles={10}
            maxFileSize={50 * 1024 * 1024} // 50MB
            accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
          >
            <PromptInputBody>
              <PromptInputAttachments>
                {(attachment) => <CustomAttachment data={attachment} />}
              </PromptInputAttachments>
              <PromptInputTextarea
                placeholder="Ask anything..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className={cn(
                  "w-full min-h-[50px] px-4 py-3 resize-none",
                  "border-0 focus:ring-0 focus:outline-none",
                  "text-gray-900 placeholder-gray-500",
                  "bg-transparent"
                )}
              />
            </PromptInputBody>
            <PromptInputToolbar className={cn("flex items-center justify-between px-4 py-3")}>
              <PromptInputTools className="flex items-center space-x-2">
                <FileUploadButton />
              </PromptInputTools>
              <PromptInputSubmit
                disabled={status === 'streaming'}
                status={status}
                className={cn(
                  "inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors",
                  status === 'streaming'
                    ? "bg-red-500 hover:bg-red-600 text-white"
                    : "bg-gray-400 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed data-[state=ready]:bg-blue-500 data-[state=ready]:hover:bg-blue-600"
                )}
              />
            </PromptInputToolbar>
          </PromptInput>

          {/* Helper text */}
          <div className="mt-2 text-xs text-gray-500 text-center">
          Searching the web, searching document outside of those in the chat, and chat history are not supported yet (but coming soon). Make sure to copy results from your chat before you exit.
          </div>
        </div>
        </div>
      </div>

      {/* Document viewer panel */}
      {documentViewer && (
        <div className="w-1/2 border-l border-gray-200">
          <InconsistencyPreview
            fileUrl={documentViewer.fileUrl}
            title={documentViewer.title}
            onClose={handleCloseDocument}
            page={documentViewer.page}
            isPDFOverride={true}
            selectedText={documentViewer.selectedText as any}
            extensionOverride={documentViewer.extension as any}
          />
        </div>
      )}
    </div>
  );
}